import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ResolverDeployment from '../../contracts/deployments/Resolver.json';
import { handleError } from 'src/utils/errors/common';
import { setResolver } from 'src/utils/contracts/registry';
import {
  setAddr,
  setContentHash,
  setPubKey,
} from 'src/utils/contracts/resolver';
import { Chain } from 'src/types/enums';
import { isValidAddress } from 'src/utils/common';
import { ACTIVE_DEPLOYMENT } from 'src/utils/contracts/common';

const Resolver: React.FC<{
  setSelectedDomain: (domain: Domain) => void;
  domain: Domain;
}> = ({ setSelectedDomain, domain }) => {
  const [resolverInput, setResolverInput] = useState<string>('');
  const [cAddressInput, setCAddressInput] = useState<string>('');
  const [pubKeyInput, setPubKeyInput] = useState<string>('');
  const [contentHashInput, setContentHashInput] = useState<string>('');

  const inputs = [
    {
      label: 'Resolver Address',
      prop: domain.resolver?.resolverAddress,
      inputValue: resolverInput,
      function: setResolverInput,
      onClick: setResolver,
      args: [domain.name.split('.'), resolverInput],
      deleteArgs: [
        domain.name.split('.'),
        '0x0000000000000000000000000000000000000000',
      ],
    },
    {
      label: 'Address',
      prop: domain.resolver?.cChainAddress,
      inputValue: cAddressInput,
      function: setCAddressInput,
      onClick: setAddr,
      args: [
        domain.name.split('.'),
        cAddressInput,
        Chain.C,
        domain.resolver?.resolverAddress,
      ],
      deleteArgs: [
        domain.name.split('.'),
        '0x0000000000000000000000000000000000000000',
        Chain.C,
        domain.resolver?.resolverAddress,
      ],
    },
    {
      label: 'Public Key',
      prop: domain.resolver?.publicKey,
      inputValue: pubKeyInput,
      function: setPubKeyInput,
      onClick: setPubKey,
      args: [
        domain.name.split('.'),
        pubKeyInput,
        domain.resolver?.resolverAddress,
      ],
      deleteArgs: [
        domain.name.split('.'),
        '',
        domain.resolver?.resolverAddress,
      ],
    },
    {
      label: 'Content Hash',
      prop: domain.resolver?.contentHash,
      inputValue: contentHashInput,
      function: setContentHashInput,
      onClick: setContentHash,
      args: [
        domain.name.split('.'),
        contentHashInput,
        domain.resolver?.resolverAddress,
      ],
      deleteArgs: [
        domain.name.split('.'),
        '0x',
        domain.resolver?.resolverAddress,
      ],
    },
  ];

  const setValue = async (
    label: string,
    funcName: (...args: any[]) => Promise<void>,
    args: any[],
    value: string,
  ) => {
    try {
      await funcName(...args);

      if (label !== 'Resolver Address' && domain.resolver) {
        setSelectedDomain({
          ...domain,
          resolver: {
            ...domain.resolver,
            [updateResolverValue(label)]: value,
          },
        });
      } else if (label === 'Resolver Address') {
        console.log('Setting Resolver Address');
        setSelectedDomain({
          ...domain,
          resolver: {
            resolverAddress: value,
            cChainAddress: '',
            xChainAddress: '',
            pChainAddress: '',
            publicKey: '',
            contentHash: '',
          },
        });
      }
    } catch (error) {
      handleError(error, `Failed to update ${label}.`);
    }
  };

  const deleteValue = async (
    label: string,
    funcName: (...args: any[]) => Promise<void>,
    args: any[],
  ) => {
    try {
      await funcName(...args);

      if (label !== 'Resolver Address' && domain.resolver) {
        setSelectedDomain({
          ...domain,
          resolver: {
            ...domain.resolver,
            [updateResolverValue(label)]: '',
          },
        });
      } else {
        setSelectedDomain({
          ...domain,
          resolver: {
            resolverAddress: '',
            cChainAddress: '',
            xChainAddress: '',
            pChainAddress: '',
            publicKey: '',
            contentHash: '',
          },
        });
      }
    } catch (error) {
      handleError(error, `Failed to update ${label}.`);
    }
  };

  const updateResolverValue = (label: string): string => {
    if (label === 'Address') {
      return 'cChainAddress';
    } else if (label === 'Public Key') {
      return 'publicKey';
    } else if (label === 'Content Hash') {
      return 'contentHash';
    }
    return '';
  };

  const setupDefaultResolver = async () => {
    await setValue(
      'Resolver Address',
      setResolver,
      [domain.name.split('.'), ResolverDeployment[ACTIVE_DEPLOYMENT].address],
      ResolverDeployment[ACTIVE_DEPLOYMENT].address,
    );
  };

  useEffect(() => {
    if (domain.resolver) {
      setResolverInput(domain.resolver.resolverAddress);
      setCAddressInput(domain.resolver.cChainAddress);
      setPubKeyInput(domain.resolver.publicKey);
      setContentHashInput(domain.resolver.contentHash);
    }
  }, [domain]);

  return (
    <Container maxWidth="sm">
      <Box mt={4}>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          direction="row"
          rowSpacing={5}
          columnSpacing={2}
        >
          {domain.resolver?.resolverAddress.length === 0 ||
          resolverInput !== ResolverDeployment[ACTIVE_DEPLOYMENT].address ? (
            <Grid item xs={12} textAlign="center" mb={2}>
              <Button
                variant="contained"
                sx={{
                  color: 'text.primary',
                  backgroundColor: 'primary.main',
                  padding: '10px 16px',
                }}
                onClick={() => setupDefaultResolver()}
              >
                Set To CNS Resolver
              </Button>
            </Grid>
          ) : null}
          {inputs.map(input => (
            <React.Fragment key={input.label}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{
                      position: 'absolute',
                      transform: 'translateY(-24px)',
                      pointerEvents: 'none',
                    }}
                  >
                    {input.label}
                  </FormLabel>
                  <TextField
                    title={
                      input.label === 'Resolver Address'
                        ? 'The address of the Resolver contract to save well-known data types'
                        : input.label === 'Address' &&
                            !isValidAddress(
                              input.inputValue,
                              input.args[2] as Chain,
                            )
                          ? `Input must be a valid ${input.label}`
                          : input.label === 'Address'
                            ? `Any address you want to associate with ${domain}`
                            : input.label === 'Public Key'
                              ? `Any public key you want to associate with ${domain}`
                              : input.label === 'Content Hash'
                                ? 'For example an IPFS content hash'
                                : ''
                    }
                    fullWidth
                    variant="outlined"
                    value={input.inputValue}
                    placeholder={`Set ${input.label}`}
                    disabled={
                      input.label !== 'Resolver Address' &&
                      domain.resolver?.resolverAddress.length === 0
                    }
                    onChange={e => input.function(e.target.value)}
                    InputLabelProps={{
                      shrink: false,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        '&:hover fieldset': {
                          borderColor: 'grey.600',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'grey.600',
                        },
                      },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  fullWidth
                  disabled={
                    (input.label !== 'Resolver Address' &&
                      domain.resolver?.resolverAddress.length === 0) ||
                    (input.label === 'Address' &&
                      !isValidAddress(input.inputValue, input.args[2] as Chain))
                  }
                  sx={{
                    color: 'text.primary',
                    backgroundColor: 'primary.main',
                    padding: '10px 16px',
                  }}
                  onClick={() =>
                    setValue(
                      input.label,
                      input.onClick,
                      input.args,
                      input.inputValue,
                    )
                  }
                >
                  {input.prop && input.prop.length > 0 ? (
                    <Typography>Update</Typography>
                  ) : (
                    <Typography>Set</Typography>
                  )}
                </Button>
                {input.prop && input.prop.length > 0 ? (
                  <IconButton
                    aria-label="delete"
                    onClick={() =>
                      deleteValue(input.label, input.onClick, input.deleteArgs)
                    }
                  >
                    <DeleteIcon sx={{ color: 'red' }} />
                  </IconButton>
                ) : null}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Resolver;
